import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useOpenOnNew from '../hooks/useOpenOnNew';
import { useAuth } from '../context/AuthContext';
import {
  getOpportunities,
  getOpportunityStages,
  getAccounts,
  getContacts,
  getUsers,
  getProducts,
  getRepFirms,
  createOpportunity,
  updateOpportunity,
  deleteOpportunity,
  bulkUpdateOpportunities,
  bulkDeleteOpportunities,
  exportOpportunitiesExcel,
  exportOpportunitiesPdf,
  createAccount,
  createContact,
  optional,
} from '../api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import SortableHeader from '../components/SortableHeader';
import usePagination from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import { TableSkeleton, CardSkeleton } from '../components/TableSkeleton';
import RowActions from '../components/RowActions';
import EmptyValue from '../components/EmptyValue';
import MobileCard from '../components/MobileCard';
import useMediaQuery from '../hooks/useMediaQuery';
import {
  Plus,
  Search,
  Target,
  Trash2,
  Edit,
  ChevronDown,
  Save,
  X,
  LayoutGrid,
  List,
  Eye,
} from 'lucide-react';
import OpportunitiesKanban from '../components/OpportunitiesKanban';
import { formatCurrency } from '../utils/formatCurrency';
import ExportMenu from '../components/ExportMenu';
import { ColumnsButton } from '../components/ColumnConfigurator';
import SavedViews from '../components/SavedViews';
import MultiSelectFilter from '../components/MultiSelectFilter';
import useColumns from '../hooks/useColumns';
import EmptyState, { EmptyStateFiltered } from '../components/EmptyState';
import TypeaheadSelect from '../components/TypeaheadSelect';
import './Opportunities.css';

function Opportunities() {
  const { canDo, canOverridePrice } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { visibleColumns } = useColumns('opportunities');
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Reference data
  const [stages, setStages] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [repFirms, setRepFirms] = useState([]);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');

  // Filters - initialize from URL params
  const [stageFilter, setStageFilter] = useState(() => {
    const param = searchParams.get('stage_id');
    return param ? param.split(',') : [];
  });
  const [stageNameFilter, setStageNameFilter] = useState(searchParams.get('stage_name') || '');
  const [stageTypeFilter, setStageTypeFilter] = useState(searchParams.get('stage') || '');
  const [ownerFilter, setOwnerFilter] = useState([]);

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMore, setShowMore] = useState(false);
  useOpenOnNew(() => setShowCreateModal(true));
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Inline editing
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  // View mode
  const [viewMode, setViewMode] = useState('table');

  // Form state
  const [formData, setFormData] = useState({
    account_id: '',
    contact_id: '',
    owner_id: '',
    stage_id: '',
    expected_close_date: '',
    rep_firm_id: '',
    notes: '',
    line_items: [],
  });

  const [accountContacts, setAccountContacts] = useState([]);

  // Typeahead state
  const [accountSearchText, setAccountSearchText] = useState('');
  const [contactSearchText, setContactSearchText] = useState('');

  // Inline create state
  const [showNewAccountForm, setShowNewAccountForm] = useState(false);
  const [newAccountName, setNewAccountName] = useState('');
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContactFirstName, setNewContactFirstName] = useState('');
  const [newContactLastName, setNewContactLastName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');

  // --- Server-side pagination for TABLE view ---
  const extraParams = useMemo(() => {
    const p = {};
    if (stageFilter.length) p.stage_id = stageFilter.join(',');
    if (ownerFilter.length) p.owner_id = ownerFilter.join(',');
    return p;
  }, [stageFilter, ownerFilter]);

  const {
    data: tableData,
    loading: tableLoading,
    page,
    setPage,
    limit,
    setLimit,
    total,
    totalPages,
    search,
    setSearch,
    sortBy,
    sortOrder,
    handleSort,
    refetch,
  } = usePagination(getOpportunities, {
    queryKey: 'opportunities',
    initialLimit: 25,
    initialSortBy: 'created_at',
    initialSortOrder: 'desc',
    extraParams,
  });

  // Apply client-side filters for stageNameFilter and stageTypeFilter
  const filteredTableData = useMemo(() => {
    let filtered = tableData;
    if (stageNameFilter) {
      filtered = filtered.filter(opp => opp.stage_name === stageNameFilter);
    }
    if (stageTypeFilter === 'open') {
      filtered = filtered.filter(opp => !opp.is_won && !opp.is_lost);
    } else if (stageTypeFilter === 'won') {
      filtered = filtered.filter(opp => opp.is_won);
    } else if (stageTypeFilter === 'lost') {
      filtered = filtered.filter(opp => opp.is_lost);
    }
    return filtered;
  }, [tableData, stageNameFilter, stageTypeFilter]);

  // --- Kanban view gets unpaginated data ---
  const [kanbanData, setKanbanData] = useState([]);
  const [kanbanLoading, setKanbanLoading] = useState(false);

  const loadKanbanData = useCallback(async () => {
    setKanbanLoading(true);
    try {
      const res = await getOpportunities({ search, stage_id: stageFilter.join(',') || undefined, owner_id: ownerFilter.join(',') || undefined });
      let data = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      // Apply client-side filters for kanban too
      if (stageNameFilter) {
        data = data.filter(opp => opp.stage_name === stageNameFilter);
      }
      if (stageTypeFilter === 'open') {
        data = data.filter(opp => !opp.is_won && !opp.is_lost);
      } else if (stageTypeFilter === 'won') {
        data = data.filter(opp => opp.is_won);
      } else if (stageTypeFilter === 'lost') {
        data = data.filter(opp => opp.is_lost);
      }
      setKanbanData(data);
    } finally {
      setKanbanLoading(false);
    }
  }, [search, stageFilter, ownerFilter, stageNameFilter, stageTypeFilter]);

  useEffect(() => {
    if (viewMode === 'kanban') loadKanbanData();
  }, [viewMode, loadKanbanData]);

  // Load reference data once
  useEffect(() => {
    loadInitialData();
  }, []);

  // Handle create modal from URL params
  useEffect(() => {
    const shouldCreate = searchParams.get('create') === 'true';
    const accountIdParam = searchParams.get('account_id');

    if (shouldCreate && accounts.length > 0) {
      setShowCreateModal(true);
      if (accountIdParam) {
        const acct = accounts.find(a => a.id === parseInt(accountIdParam));
        handleAccountChange(accountIdParam, acct?.name || '');
      }
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('create');
      newParams.delete('account_id');
      window.history.replaceState({}, '', `${window.location.pathname}${newParams.toString() ? '?' + newParams.toString() : ''}`);
    }
  }, [searchParams, accounts]);

  const loadInitialData = async () => {
    try {
      const [stagesRes, accountsRes, usersRes, productsRes, repFirmsRes] = await Promise.all([
        getOpportunityStages(),
        optional(getAccounts()),
        getUsers(),
        optional(getProducts()),
        optional(getRepFirms()),
      ]);
      setStages(stagesRes.data);
      setAccounts(accountsRes.data);
      setUsers(usersRes.data.filter(u => u.active));
      setProducts(productsRes.data.filter(p => p.active));
      setRepFirms(repFirmsRes.data);
    } catch (err) {
      console.error('Failed to load initial data:', err);
    }
  };

  const handleAccountChange = async (accountId, accountName) => {
    setFormData(prev => ({ ...prev, account_id: accountId, contact_id: '' }));
    setAccountSearchText(accountName || '');
    setContactSearchText('');
    if (accountId) {
      try {
        const res = await getContacts({ account_id: accountId });
        setAccountContacts(res.data);
      } catch (err) {
        console.error('Failed to load contacts for account:', err);
        setAccountContacts([]);
      }
    } else {
      setAccountContacts([]);
    }
  };

  const handleCreateOpportunity = async (e) => {
    e.preventDefault();

    if (!formData.account_id) {
      setModalError('Account is required');
      return;
    }

    try {
      const response = await createOpportunity(formData);
      setShowCreateModal(false);
      resetForm();
      navigate(`/opportunities/${response.data.id}`);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to create opportunity');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteOpportunity(deleteId);
      setDeleteId(null);
      setShowDeleteConfirm(false);
      refetch();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete opportunity');
    }
  };

  const handleInlineEdit = (opp) => {
    setEditingId(opp.id);
    setEditData({
      name: opp.name,
      stage_id: opp.stage_id || '',
      owner_id: opp.owner_id || '',
      expected_close_date: opp.expected_close_date || '',
    });
  };

  const handleSaveInline = async () => {
    try {
      await updateOpportunity(editingId, editData);
      setEditingId(null);
      setEditData({});
      refetch();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update opportunity');
    }
  };

  const handleCancelInline = () => {
    setEditingId(null);
    setEditData({});
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredTableData.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredTableData.map(o => o.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkUpdate = async (updates) => {
    try {
      await bulkUpdateOpportunities(selectedIds, updates);
      setSelectedIds([]);
      setShowBulkActions(false);
      refetch();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update opportunities');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteOpportunities(selectedIds);
      setSelectedIds([]);
      setShowDeleteConfirm(false);
      refetch();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete opportunities');
    }
  };

  const resetForm = () => {
    setModalError('');
    setFormData({
      account_id: '',
      contact_id: '',
      owner_id: '',
      stage_id: '',
      expected_close_date: '',
      rep_firm_id: '',
      notes: '',
      line_items: [],
    });
    setAccountContacts([]);
    setAccountSearchText('');
    setContactSearchText('');
    setShowNewAccountForm(false);
    setNewAccountName('');
    setShowNewContactForm(false);
    setNewContactFirstName('');
    setNewContactLastName('');
    setNewContactEmail('');
  };

  const handleCreateNewAccount = async () => {
    if (!newAccountName.trim()) return;
    try {
      const res = await createAccount({ name: newAccountName.trim() });
      const newAcct = res.data;
      setAccounts(prev => [...prev, { id: newAcct.id, name: newAcct.name, contacts: [] }]);
      setFormData(prev => ({ ...prev, account_id: newAcct.id, contact_id: '' }));
      setAccountSearchText(newAcct.name);
      setAccountContacts([]);
      setContactSearchText('');
      setShowNewAccountForm(false);
      setNewAccountName('');
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to create account');
    }
  };

  const handleCreateNewContact = async () => {
    if (!newContactFirstName.trim() || !formData.account_id) return;
    try {
      const res = await createContact({
        first_name: newContactFirstName.trim(),
        last_name: newContactLastName.trim(),
        email: newContactEmail.trim() || undefined,
        account_id: formData.account_id,
      });
      const c = res.data;
      const contactName = `${c.first_name} ${c.last_name || ''}`.trim();
      const newContact = { id: c.id, name: contactName };
      setAccountContacts(prev => [...prev, newContact]);
      setFormData(prev => ({ ...prev, contact_id: c.id }));
      setContactSearchText(contactName);
      setShowNewContactForm(false);
      setNewContactFirstName('');
      setNewContactLastName('');
      setNewContactEmail('');
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to create contact');
    }
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { product_id: '', product_name: '', quantity: 1, unit_price: 0 }],
    }));
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => {
        if (i === index) {
          const updated = { ...item, [field]: value };
          if (field === 'product_id' && value) {
            const product = products.find(p => p.id === parseInt(value));
            if (product) {
              updated.product_name = product.name;
              updated.unit_price = product.price;
            }
          }
          return updated;
        }
        return item;
      }),
    }));
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotal = () => {
    return formData.line_items.reduce((sum, item) => {
      return sum + (item.quantity || 0) * (item.unit_price || 0);
    }, 0);
  };

  const handleKanbanStageChange = async (opportunityId, newStageId) => {
    try {
      await updateOpportunity(opportunityId, { stage_id: newStageId });
      loadKanbanData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update opportunity stage');
    }
  };

  // Render cell for both table and mobile card
  const renderCell = (col, opp) => {
    const isEditing = editingId === opp.id;

    switch (col.key) {
      case 'name':
        return isEditing ? (
          <input type="text" className="form-input form-input-sm" value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
        ) : (
          <Link to={`/opportunities/${opp.id}`} className="opportunity-name-link">{opp.name}</Link>
        );
      case 'account_name':
        return opp.account_name ? <Link to={`/accounts/${opp.account_id}`}>{opp.account_name}</Link> : <EmptyValue />;
      case 'stage_name':
        return isEditing ? (
          <select className="form-select form-select-sm" value={editData.stage_id} onChange={(e) => setEditData({ ...editData, stage_id: e.target.value })}>
            <option value="">Select Stage</option>
            {stages.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
          </select>
        ) : (
          opp.stage_name ? <span className={`badge ${opp.is_won ? 'badge-success' : opp.is_lost ? 'badge-danger' : 'badge-primary'}`}>{opp.stage_name}</span> : <EmptyValue />
        );
      case 'amount':
        return <span className="opportunity-amount">{formatCurrency(opp.amount || opp.calculated_amount)}</span>;
      case 'owner_name':
        return isEditing ? (
          <select className="form-select form-select-sm" value={editData.owner_id} onChange={(e) => setEditData({ ...editData, owner_id: e.target.value })}>
            <option value="">Select Owner</option>
            {users.map(u => (<option key={u.id} value={u.id}>{u.name}</option>))}
          </select>
        ) : (opp.owner_name ? opp.owner_name : <EmptyValue />);
      case 'expected_close_date':
        return isEditing ? (
          <input type="date" className="form-input form-input-sm" value={editData.expected_close_date} onChange={(e) => setEditData({ ...editData, expected_close_date: e.target.value })} />
        ) : (opp.expected_close_date ? new Date(opp.expected_close_date).toLocaleDateString() : <EmptyValue />);
      case 'contact_name':
        return opp.contact_name ? opp.contact_name : <EmptyValue />;
      case 'rep_firm_name':
        return opp.rep_firm_name ? opp.rep_firm_name : <EmptyValue />;
      case 'created_at':
        return opp.created_at ? new Date(opp.created_at).toLocaleDateString() : <EmptyValue />;
      default:
        return opp[col.key] ? opp[col.key] : <EmptyValue />;
    }
  };

  // Build row actions for an opportunity
  const getRowActions = (opp) => {
    const isEditing = editingId === opp.id;
    if (isEditing) return null; // Show Save/Cancel buttons directly when editing

    const actions = [
      { key: 'view', label: 'View', icon: Eye, onClick: () => navigate(`/opportunities/${opp.id}`) },
    ];

    if (canDo('opportunities', 'edit')) {
      actions.push({ key: 'edit', label: 'Edit Inline', icon: Edit, onClick: () => handleInlineEdit(opp) });
    }

    if (canDo('opportunities', 'delete')) {
      actions.push({ divider: true });
      actions.push({ key: 'delete', label: 'Delete', icon: Trash2, variant: 'danger', onClick: () => { setDeleteId(opp.id); setShowDeleteConfirm(true); } });
    }

    return actions;
  };

  return (
    <div className={`opportunities-page${viewMode === 'kanban' ? ' kanban-active' : ''}`}>
      <div className="page-header">
        <h1 className="page-title">Opportunities</h1>
        <div className="page-actions">
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              <List size={16} />
              Table
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => setViewMode('kanban')}
            >
              <LayoutGrid size={16} />
              Kanban
            </button>
          </div>
          <SavedViews entityType="opportunities" current={{ search, stageFilter, stageNameFilter, stageTypeFilter, ownerFilter, sortBy: sortBy, sortOrder: sortOrder }} onApply={(v) => { setSearch(v.search || ''); setStageFilter(v.stageFilter || []); setStageNameFilter(v.stageNameFilter || ''); setStageTypeFilter(v.stageTypeFilter || ''); setOwnerFilter(v.ownerFilter || []); if (v.sortBy) handleSort(v.sortBy, v.sortOrder || 'desc'); }} />
          <ColumnsButton entityType="opportunities" />
          <ExportMenu
            onExportExcel={exportOpportunitiesExcel}
            onExportPdf={exportOpportunitiesPdf}
            entityName="Opportunities"
          />
          {canDo('opportunities', 'create') && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} />
              New Opportunity
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert" aria-live="assertive">{error}</div>}

      {/* Search and Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="filter-bar">
            <div className="search-input">
              <Search size={18} className="search-input-icon" aria-hidden="true" />
              <input
                type="text"
                className="form-input"
                placeholder="Search opportunities..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search opportunities"
              />
            </div>
            <MultiSelectFilter
              label="Stages"
              options={stages}
              selected={stageFilter}
              onChange={setStageFilter}
              className="filter-dropdown-md"
              ariaLabel="Filter by stage"
            />
            <MultiSelectFilter
              label="Owners"
              options={users}
              selected={ownerFilter}
              onChange={setOwnerFilter}
              className="filter-dropdown-md"
              ariaLabel="Filter by owner"
            />
            {(search || stageFilter.length > 0 || ownerFilter.length > 0) && (
              <button
                className="btn-clear-filters"
                onClick={() => { setSearch(''); setStageFilter([]); setOwnerFilter([]); }}
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && viewMode === 'table' && (
        <div className="bulk-actions-bar">
          <span>{selectedIds.length} selected</span>
          <div className="bulk-actions">
            {canDo('opportunities', 'edit') && (
              <>
                <div className="dropdown">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowBulkActions(showBulkActions === 'stage' ? false : 'stage')}
                  >
                    Change Stage
                    <ChevronDown size={16} />
                  </button>
                  {showBulkActions === 'stage' && (
                    <div className="dropdown-menu">
                      {stages.map(s => (
                        <button
                          key={s.id}
                          className="dropdown-item"
                          onClick={() => handleBulkUpdate({ stage_id: s.id })}
                        >
                          {s.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="dropdown">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowBulkActions(showBulkActions === 'owner' ? false : 'owner')}
                  >
                    Change Owner
                    <ChevronDown size={16} />
                  </button>
                  {showBulkActions === 'owner' && (
                    <div className="dropdown-menu">
                      {users.map(u => (
                        <button
                          key={u.id}
                          className="dropdown-item"
                          onClick={() => handleBulkUpdate({ owner_id: u.id })}
                        >
                          {u.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            {canDo('opportunities', 'delete') && (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 size={16} />
                Delete
              </button>
            )}
          </div>
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' ? (
        kanbanLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <OpportunitiesKanban
            opportunities={kanbanData}
            stages={stages}
            onStageChange={handleKanbanStageChange}
            formatCurrency={formatCurrency}
            canEdit={canDo('opportunities', 'edit')}
          />
        )
      ) : isMobile ? (
        /* Mobile Card View */
        <div>
          {tableLoading ? (
            <CardSkeleton count={4} />
          ) : filteredTableData.length === 0 ? (
            <div className="card">
              <div className="card-body">
                {search || stageFilter || ownerFilter ? (
                  <EmptyStateFiltered
                    onClearFilters={() => { setSearch(''); setStageFilter(''); setOwnerFilter(''); }}
                  />
                ) : (
                  <EmptyState
                    icon={Target}
                    title="No opportunities yet"
                    description="Create your first opportunity to start tracking your sales pipeline."
                    actionLabel={canDo('opportunities', 'create') ? 'New Opportunity' : undefined}
                    onAction={canDo('opportunities', 'create') ? () => setShowCreateModal(true) : undefined}
                    actionIcon={Plus}
                  />
                )}
              </div>
            </div>
          ) : (
            <>
              {filteredTableData.map(opp => {
                const isEditing = editingId === opp.id;
                const actions = isEditing ? (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button className="btn btn-icon btn-success" onClick={handleSaveInline} title="Save" aria-label="Save changes"><Save size={16} /></button>
                    <button className="btn btn-icon" onClick={handleCancelInline} title="Cancel" aria-label="Cancel editing"><X size={16} /></button>
                  </div>
                ) : (
                  <RowActions actions={getRowActions(opp)} id={opp.id} />
                );

                return (
                  <MobileCard
                    key={opp.id}
                    columns={visibleColumns}
                    data={opp}
                    renderCell={(col, data) => renderCell(col, data)}
                    selected={selectedIds.includes(opp.id)}
                    onSelect={() => toggleSelect(opp.id)}
                    actions={actions}
                    primaryField="name"
                  />
                );
              })}
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            </>
          )}
        </div>
      ) : (
        /* Desktop Table View */
        <div className="card">
          <div className="table-container">
            <table className="table" aria-label="Opportunities list">
              <thead>
                <tr>
                  <th scope="col" style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selectedIds.length === filteredTableData.length && filteredTableData.length > 0}
                      onChange={toggleSelectAll}
                      aria-label="Select all opportunities"
                    />
                  </th>
                  {visibleColumns.map(col => (
                    <SortableHeader key={col.key} label={col.label} field={col.key} sortField={sortBy} sortDirection={sortOrder} onSort={handleSort} />
                  ))}
                  <th scope="col" className="col-actions"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <TableSkeleton columns={visibleColumns.length} rows={8} hasCheckbox hasActions />
                ) : filteredTableData.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 2}>
                      {search || stageFilter || ownerFilter ? (
                        <EmptyStateFiltered
                          onClearFilters={() => { setSearch(''); setStageFilter(''); setOwnerFilter(''); }}
                        />
                      ) : (
                        <EmptyState
                          icon={Target}
                          title="No opportunities yet"
                          description="Create your first opportunity to start tracking your sales pipeline."
                          actionLabel={canDo('opportunities', 'create') ? 'New Opportunity' : undefined}
                          onAction={canDo('opportunities', 'create') ? () => setShowCreateModal(true) : undefined}
                          actionIcon={Plus}
                        />
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredTableData.map(opp => {
                    const isEditing = editingId === opp.id;

                    return (
                      <tr key={opp.id}>
                        <td>
                          <input type="checkbox" className="checkbox" checked={selectedIds.includes(opp.id)} onChange={() => toggleSelect(opp.id)} />
                        </td>
                        {visibleColumns.map(col => (
                          <td key={col.key}>{renderCell(col, opp)}</td>
                        ))}
                        <td>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button className="btn btn-icon btn-success" onClick={handleSaveInline} title="Save" aria-label="Save changes"><Save size={16} /></button>
                              <button className="btn btn-icon" onClick={handleCancelInline} title="Cancel" aria-label="Cancel editing"><X size={16} /></button>
                            </div>
                          ) : (
                            <RowActions actions={getRowActions(opp)} id={opp.id} />
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {!tableLoading && filteredTableData.length > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          )}
        </div>
      )}

      {/* Create Opportunity Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="New Opportunity"
        size="lg"
      >
        <form onSubmit={handleCreateOpportunity}>
          <div className="modal-body">
            {modalError && <div className="alert alert-error" role="alert">{modalError}</div>}
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Account *</label>
                {showNewAccountForm ? (
                  <div className="inline-create-form">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Account name *"
                      value={newAccountName}
                      onChange={(e) => setNewAccountName(e.target.value)}
                      autoFocus
                    />
                    <div className="inline-create-actions">
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleCreateNewAccount} disabled={!newAccountName.trim()}>Create</button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowNewAccountForm(false); setNewAccountName(''); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <TypeaheadSelect
                    id="opp-account"
                    required
                    placeholder="Type to search accounts..."
                    items={accounts}
                    value={formData.account_id}
                    displayValue={accountSearchText}
                    onChange={(id, name) => handleAccountChange(id, name)}
                    onAddNew={() => setShowNewAccountForm(true)}
                    addNewLabel="Add New Account"
                  />
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Contact</label>
                {showNewContactForm ? (
                  <div className="inline-create-form">
                    <div className="grid grid-cols-2">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="First name *"
                        value={newContactFirstName}
                        onChange={(e) => setNewContactFirstName(e.target.value)}
                        autoFocus
                      />
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Last name"
                        value={newContactLastName}
                        onChange={(e) => setNewContactLastName(e.target.value)}
                      />
                    </div>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="Email (optional)"
                      value={newContactEmail}
                      onChange={(e) => setNewContactEmail(e.target.value)}
                      style={{ marginTop: '8px' }}
                    />
                    <div className="inline-create-actions">
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleCreateNewContact} disabled={!newContactFirstName.trim()}>Create</button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowNewContactForm(false); setNewContactFirstName(''); setNewContactLastName(''); setNewContactEmail(''); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <TypeaheadSelect
                    id="opp-contact"
                    placeholder={formData.account_id ? "Type to search contacts..." : "Select an account first"}
                    items={accountContacts}
                    value={formData.contact_id}
                    displayValue={contactSearchText}
                    onChange={(id, name) => {
                      setFormData(prev => ({ ...prev, contact_id: id }));
                      setContactSearchText(name);
                    }}
                    onAddNew={formData.account_id ? () => setShowNewContactForm(true) : undefined}
                    addNewLabel="Add New Contact"
                    disabled={!formData.account_id}
                  />
                )}
              </div>
            </div>

            {showMore ? (
              <>
            <div className="grid grid-cols-2">
            <div className="form-group">
                <label className="form-label">Owner</label>
                <select
                  className="form-select"
                  value={formData.owner_id}
                  onChange={(e) => setFormData({ ...formData, owner_id: e.target.value })}
                >
                  <option value="">Select Owner</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Stage</label>
                <select
                  className="form-select"
                  value={formData.stage_id}
                  onChange={(e) => setFormData({ ...formData, stage_id: e.target.value })}
                >
                  <option value="">Select Stage</option>
                  {stages.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Expected Close Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.expected_close_date}
                  onChange={(e) => setFormData({ ...formData, expected_close_date: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Rep Firm</label>
                <select
                  className="form-select"
                  value={formData.rep_firm_id}
                  onChange={(e) => setFormData({ ...formData, rep_firm_id: e.target.value })}
                >
                  <option value="">Select Rep Firm</option>
                  {repFirms.map(rf => (
                    <option key={rf.id} value={rf.id}>{rf.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-textarea"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
              </>
            ) : (
              <p className="form-more">
                <button type="button" className="btn-link" onClick={() => setShowMore(true)}>More details…</button>
                <span className="text-muted text-sm"> — everything else can be filled in on the record.</span>
              </p>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Create Opportunity
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setDeleteId(null);
        }}
        onConfirm={deleteId ? handleDelete : handleBulkDelete}
        title={deleteId ? 'Delete Opportunity' : 'Delete Opportunities'}
        message={deleteId
          ? 'Are you sure you want to delete this opportunity? This action cannot be undone.'
          : `Are you sure you want to delete ${selectedIds.length} opportunity(s)? This action cannot be undone.`
        }
        confirmText="Delete"
      />
    </div>
  );
}

export default Opportunities;
